import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Container,
  merge,
  Spinner,
  Theme,
  ThemeProvider,
  useColorMode,
} from 'theme-ui';

import Api from './api';
import { ContainsComponent } from './components/Contains';
import { LanguageComponent } from './components/Language';
import { LengthComponent } from './components/Length';
import { Modal } from './components/Modal';
import ResultsComponent from './components/Results';
import { SearchComponent } from './components/Search';
import Settings from './components/Settings';
import { SettingsButton } from './components/SettingsButton';
import { AppearanceValue, myTheme, ThemePresets, ThemeValue } from './theme';
import { AppState, ContainsLogicEnum, GetWordsParams, LangEnum } from './types';

export const LOCAL_STORAGE_QUERY_KEY = 'query';
export const LOCAL_STORAGE_THEME_KEY = 'settings.theme';
export const LOCAL_STORAGE_QUERY_SETTINGS_KEY = 'settings.query';

const fetchWords = async (
  state: AppState,
  setState: React.Dispatch<React.SetStateAction<AppState>>,
  saveQuery: boolean,
) => {
  if (state.isLoading) {
    return;
  }

  setState({ ...state, isLoading: true });

  const { page, range, pages, total, words } = await Api.getWords(
    {
      lang: state.lang,
      isWithDefinition: state.isWithDefinition,
      isCombineDefinition: state.isCombineDefinition,
      search: state.search,
      isRegex: state.isRegex,
      minLength: state.minLength,
      maxLength: state.maxLength,
      contains: state.contains,
      containsLogic: state.containsLogic,
      page: state.isResetPage ? 1 : state.page,
    },
    saveQuery,
  );

  setState({
    ...state,
    total,
    words,
    pages,
    range,
    page,
    isLoading: false,
    isResetPage: false,
  });
};

const ProviderWrapper: React.FC = () => {
  const currentTheme = (localStorage.getItem(LOCAL_STORAGE_THEME_KEY) ??
    ThemePresets.system.value) as ThemeValue;
  const [theme, setTheme] = useState(
    merge(ThemePresets[currentTheme].theme, myTheme as Theme),
  );

  return (
    <ThemeProvider theme={theme}>
      <App
        currentTheme={currentTheme}
        setGlobalTheme={newTheme => {
          setTheme(merge(newTheme, myTheme as Theme));
        }}
      />
    </ThemeProvider>
  );
};

const savedQueryLS = localStorage.getItem(LOCAL_STORAGE_QUERY_KEY);

const App: React.FC<{
  currentTheme: ThemeValue;
  setGlobalTheme: (theme: Theme) => void;
}> = ({ currentTheme, setGlobalTheme }) => {
  const savedParams = (
    savedQueryLS ? JSON.parse(savedQueryLS) : {}
  ) as GetWordsParams;

  const [appState, setAppState] = useState<AppState>({
    lang: savedParams.lang ?? LangEnum.EN,
    isWithDefinition: savedParams.isWithDefinition ?? false,
    isCombineDefinition: savedParams.isCombineDefinition ?? false,
    search: savedParams.search ?? '',
    isRegex: savedParams.isRegex ?? false,
    minLength: savedParams.minLength ?? '',
    maxLength: savedParams.maxLength ?? '',
    contains: savedParams.contains ?? '',
    containsLogic: savedParams.containsLogic ?? ContainsLogicEnum.AND,
    page: 1,
    words: [],
    total: 0,
    pages: [],
    range: '0 - 0',
    isLoading: false,
    isSettingsOpened: false,
    isResetPage: false,
  });

  const [colorMode, setColorMode] = useColorMode();

  const [settings, setSettings] = useState<{
    theme: ThemeValue;
    saveQueryParams: boolean;
  }>({
    theme: currentTheme,
    saveQueryParams: JSON.parse(
      localStorage.getItem(LOCAL_STORAGE_QUERY_SETTINGS_KEY) ?? 'true',
    ) as boolean,
  });

  useEffect(() => {
    setAppState({ ...appState, isResetPage: true });
  }, [
    appState.contains,
    appState.containsLogic,
    appState.isCombineDefinition,
    appState.isRegex,
    appState.isWithDefinition,
    appState.lang,
    appState.maxLength,
    appState.minLength,
    appState.search,
  ]);

  return (
    <Container py={3}>
      <LanguageComponent
        lang={appState.lang}
        isWithDefinition={appState.isWithDefinition}
        isCombineDefinition={appState.isCombineDefinition}
        onChange={changed => setAppState({ ...appState, ...changed })}
      />

      <SearchComponent
        search={appState.search}
        isRegex={appState.isRegex}
        onChange={changed => setAppState({ ...appState, ...changed })}
      />

      <LengthComponent
        minLength={appState.minLength}
        maxLength={appState.maxLength}
        onChange={changed => setAppState({ ...appState, ...changed })}
      />

      <ContainsComponent
        contains={appState.contains}
        containsLogic={appState.containsLogic}
        onChange={changed => setAppState({ ...appState, ...changed })}
      />

      <Box mt={4} ml={3} sx={{ display: 'flex', alignItems: 'center' }}>
        <Button
          disabled={appState.isLoading}
          onClick={() =>
            fetchWords(appState, setAppState, settings.saveQueryParams)
          }>
          Find
        </Button>

        {appState.isLoading ? <Spinner ml={2} size={32} /> : null}
      </Box>

      <Box
        sx={
          appState.isLoading
            ? {
                opacity: 0.6,
                pointerEvents: 'none',
              }
            : undefined
        }>
        <ResultsComponent
          page={appState.page}
          pages={appState.pages}
          range={appState.range}
          total={appState.total}
          words={appState.words}
          onPageSelect={page =>
            fetchWords(
              { ...appState, page },
              setAppState,
              settings.saveQueryParams,
            )
          }
        />
      </Box>

      <SettingsButton
        onClick={() =>
          setAppState({
            ...appState,
            isSettingsOpened: !appState.isSettingsOpened,
          })
        }
      />

      <Modal
        isOpen={appState.isSettingsOpened}
        onClose={() =>
          setAppState({
            ...appState,
            isSettingsOpened: !appState.isSettingsOpened,
          })
        }>
        <Settings
          appearance={colorMode as AppearanceValue}
          theme={settings.theme}
          saveQueryParams={settings.saveQueryParams}
          onAppearanceChange={appearance => {
            setColorMode(appearance);
          }}
          onThemeChange={theme => {
            setSettings({ ...settings, theme });
            setGlobalTheme(ThemePresets[theme].theme);
            localStorage.setItem(LOCAL_STORAGE_THEME_KEY, theme);
          }}
          onSaveQueryParamsChange={value => {
            setSettings({ ...settings, saveQueryParams: value });
            localStorage.setItem(
              LOCAL_STORAGE_QUERY_SETTINGS_KEY,
              value.toString(),
            );

            if (!value) {
              localStorage.setItem(LOCAL_STORAGE_QUERY_KEY, '');
            }
          }}
        />
      </Modal>
    </Container>
  );
};

export default ProviderWrapper;
